import { useTweenedNumber } from '../../hooks/useTweenedNumber';

interface CapMeterProps {
  label: string;
  current: number;
  cap: number;
  reached: boolean;
  size?: 'sm' | 'md';
}

/**
 * A small "n/cap" bar for the two GMS crystal-sale caps (14/character, 180/world). Caps warn, never
 * block (`BossService` javadoc, PLAN.md §5) — this is purely informational, so it never disables
 * anything, it just turns amber/rose when full.
 */
export function CapMeter({ label, current, cap, reached, size = 'md' }: CapMeterProps) {
  const pct = cap > 0 ? Math.min(100, Math.round((current / cap) * 100)) : 0;
  const barHeight = size === 'sm' ? 'h-1.5' : 'h-2';
  // REV29 (docs/specs/REV29-motion-patterns.md §4): only the readout tweens. `aria-valuenow` and
  // the warning colouring stay bound to the real `current` so assistive tech and the at-cap color
  // never lag behind the actual value.
  const tweenedCurrent = useTweenedNumber(current, 300);
  return (
    <div className="min-w-[6rem]">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-text-faint">
          {label}
        </span>
        <span
          className={[
            'text-xs font-semibold tabular-nums',
            reached ? 'text-warning' : 'text-crystal',
          ].join(' ')}
        >
          {tweenedCurrent}/{cap}
        </span>
      </div>
      <div className={['mt-1 w-full overflow-hidden rounded-full bg-bg-elevated-hover', barHeight].join(' ')}>
        <div
          role="progressbar"
          aria-label={label}
          aria-valuenow={current}
          aria-valuemin={0}
          aria-valuemax={cap}
          className={['h-full rounded-full transition-all', reached ? 'bg-warning' : 'bg-crystal'].join(' ')}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
